import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  deleteDoc, 
  doc, 
  addDoc, 
  serverTimestamp,
  getDocs,
  limit,
  startAfter,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig.jsx';

/**
 * Admin 쪽지 관리 서비스
 */
class MessageService {
  constructor() {
    this.messagesCollection = 'privateMessages';
    this.usersCollection = 'users';
  }

  /**
   * 모든 메시지 조회 (실시간) - 통계용으로만 사용
   */
  async getAllMessages(callback) {
    try {
      const messagesRef = collection(db, this.messagesCollection);
      const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(100));
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const messages = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(messages);
      }, (error) => {
        console.error('메시지 조회 실패:', error);
        callback([]);
      });

      return unsubscribe;
    } catch (error) {
      console.error('메시지 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 페이지네이션으로 메시지 조회
   * @param {number} pageSize - 페이지당 메시지 수 (기본값: 10)
   * @param {DocumentSnapshot|null} lastDoc - 마지막 문서 (다음 페이지용)
   * @param {string} filter - 필터 ('all', 'unread', 'read')
   * @param {string} searchQuery - 검색어
   * @returns {Promise<{messages: Array, lastDoc: DocumentSnapshot|null, hasMore: boolean, total: number}>}
   */
  async getMessagesPaginated(pageSize = 10, lastDoc = null, filter = 'all', searchQuery = '') {
    try {
      console.log('📄 페이지네이션 메시지 조회:', { pageSize, hasLastDoc: !!lastDoc, filter, searchQuery });
      
      const messagesRef = collection(db, this.messagesCollection);
      let q = query(messagesRef, orderBy('timestamp', 'desc'));
      
      // 마지막 문서가 있으면 그 이후부터 조회
      if (lastDoc) {
        q = query(messagesRef, orderBy('timestamp', 'desc'), startAfter(lastDoc), limit(pageSize));
      } else {
        q = query(messagesRef, orderBy('timestamp', 'desc'), limit(pageSize));
      }
      
      const snapshot = await getDocs(q);
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // 공지사항 메시지 제외
      const personalMessages = messages.filter(msg => {
        const isNoticeMessage = msg.type === 'notice_message' || msg.noticeId;
        return !isNoticeMessage;
      });
      
      // 필터 적용
      let filteredMessages = personalMessages;
      if (filter === 'unread') {
        filteredMessages = personalMessages.filter(msg => !msg.read);
      } else if (filter === 'read') {
        filteredMessages = personalMessages.filter(msg => msg.read);
      }
      
      // 검색어 필터링
      if (searchQuery) {
        const queryLower = searchQuery.toLowerCase();
        filteredMessages = filteredMessages.filter(msg =>
          msg.content?.toLowerCase().includes(queryLower) ||
          msg.senderName?.toLowerCase().includes(queryLower) ||
          msg.recipientName?.toLowerCase().includes(queryLower)
        );
      }
      
      const lastDocument = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      const hasMore = snapshot.docs.length === pageSize && filteredMessages.length > 0;
      
      // 전체 개수는 별도로 조회하지 않고, 클라이언트에서 추정
      // (실제로는 통계용 getAllMessages를 사용하므로 여기서는 null 반환)
      // 클라이언트에서 totalMessages를 별도로 관리
      
      console.log('✅ 페이지네이션 결과:', {
        returned: filteredMessages.length,
        hasMore,
        rawCount: messages.length
      });
      
      return {
        messages: filteredMessages,
        lastDoc: lastDocument,
        hasMore,
        total: null // 클라이언트에서 별도 관리
      };
    } catch (error) {
      console.error('❌ 페이지네이션 메시지 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 사용자 검색 (검색어 기반)
   */
  async searchUsers(searchQuery, limit = 20) {
    try {
      console.log('🔍 사용자 검색 시작:', searchQuery);
      const appId = 'kairos-platform-1ffde';
      const usersData = [];
      const processedUserIds = new Set();
      
      // 실제 사용자 데이터가 있는 경로들
      const userCollectionPaths = [
        `artifacts/${appId}/users`,  // 메인 경로
        'users'                       // 직접 경로 (백업용)
      ];
      
      // 각 경로에서 사용자 정보 수집
      for (const collectionPath of userCollectionPaths) {
        try {
          console.log(`📍 경로 검색: ${collectionPath}`);
          const usersRef = collection(db, collectionPath);
          const snapshot = await getDocs(usersRef);
          console.log(`📊 ${collectionPath} 문서 수:`, snapshot.docs.length);
          
          for (const userDoc of snapshot.docs) {
            const userData = userDoc.data();
            const userId = userDoc.id;
            
            // 이미 처리된 사용자인지 확인
            if (processedUserIds.has(userId)) {
              continue;
            }
            
            // Admin 사용자 제외 (일반 사용자만 관리)
            const isAdmin = userData.role === 'admin' || userData.admin === true || userData.isAdmin === true;
            if (isAdmin) {
              continue;
            }
            
            // 사용자 정보 구성
            const userInfo = {
              id: userId,
              email: userData.email || userData.emailAddress || userId,
              displayName: userData.fullName || userData.name || userData.displayName || userData.email?.split('@')[0] || '이름 없음',
              name: userData.fullName || userData.name || userData.displayName || userData.email?.split('@')[0] || '이름 없음',
              fullName: userData.fullName || userData.name || userData.displayName || userData.email?.split('@')[0] || '이름 없음',
              role: userData.role || 'user',
              active: userData.active !== false,
              createdAt: userData.createdAt || userData.created_at || userData.timestamp || new Date(),
              source: collectionPath,
              ...userData
            };
            
            // 검색어와 매칭되는지 확인
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch = 
              userInfo.name.toLowerCase().includes(searchLower) ||
              userInfo.email.toLowerCase().includes(searchLower) ||
              userInfo.displayName.toLowerCase().includes(searchLower);
            
            if (matchesSearch) {
              usersData.push(userInfo);
              processedUserIds.add(userId);
              
              // 제한된 수만큼만 반환
              if (usersData.length >= limit) {
                break;
              }
            }
          }
          
          // 제한된 수만큼 찾았으면 중단
          if (usersData.length >= limit) {
            break;
          }
        } catch (error) {
          console.warn(`❌ ${collectionPath} 로드 실패:`, error.message);
        }
      }
      
      console.log('✅ 검색 결과:', usersData.length, '명');
      return usersData;
    } catch (error) {
      console.error('❌ 사용자 검색 실패:', error);
      return [];
    }
  }

  /**
   * 사용자 목록 조회 (전체 - 기존 함수 유지)
   */
  async getUsers() {
    try {
      console.log('🔍 사용자 컬렉션 조회 시작...');
      const appId = 'kairos-platform-1ffde';
      const usersData = [];
      const processedUserIds = new Set();
      
      // 실제 사용자 데이터가 있는 경로들
      const userCollectionPaths = [
        `artifacts/${appId}/users`,  // 메인 경로
        'users'                       // 직접 경로 (백업용)
      ];
      
      // 각 경로에서 사용자 정보 수집
      for (const collectionPath of userCollectionPaths) {
        try {
          console.log(`📍 경로 검색: ${collectionPath}`);
          const usersRef = collection(db, collectionPath);
          const snapshot = await getDocs(usersRef);
          console.log(`📊 ${collectionPath} 문서 수:`, snapshot.docs.length);
          
          for (const userDoc of snapshot.docs) {
            const userData = userDoc.data();
            const userId = userDoc.id;
            
            // 이미 처리된 사용자인지 확인
            if (processedUserIds.has(userId)) {
              console.log(`⏭️ 이미 처리된 사용자: ${userId}`);
              continue;
            }
            
            // Admin 사용자 제외 (일반 사용자만 관리)
            const isAdmin = userData.role === 'admin' || userData.admin === true || userData.isAdmin === true;
            if (isAdmin) {
              console.log(`🚫 Admin 사용자 제외: ${userData.email} (${userData.role})`);
              continue;
            }
            
            console.log(`👤 사용자 발견: ${userId} (${collectionPath})`);
            
            // 사용자 정보 구성
            const userInfo = {
              id: userId,
              email: userData.email || userData.emailAddress || userId,
              displayName: userData.fullName || userData.name || userData.displayName || userData.email?.split('@')[0] || '이름 없음',
              name: userData.fullName || userData.name || userData.displayName || userData.email?.split('@')[0] || '이름 없음',
              fullName: userData.fullName || userData.name || userData.displayName || userData.email?.split('@')[0] || '이름 없음',
              role: userData.role || 'user',
              active: userData.active !== false,
              createdAt: userData.createdAt || userData.created_at || userData.timestamp || new Date(),
              source: collectionPath,
              ...userData
            };
            
            usersData.push(userInfo);
            processedUserIds.add(userId);
            console.log(`✅ 사용자 추가 완료: ${userInfo.name} (${userInfo.email})`);
          }
        } catch (error) {
          console.warn(`❌ ${collectionPath} 로드 실패:`, error.message);
          // 개별 경로 실패는 전체를 중단시키지 않음
        }
      }
      
      console.log('✅ 최종 결과:');
      console.log(`📊 총 발견된 사용자: ${usersData.length}명`);
      
      return usersData;
    } catch (error) {
      console.error('❌ 사용자 조회 실패:', error);
      // 에러가 발생해도 빈 배열을 반환하여 앱이 중단되지 않도록 함
      return [];
    }
  }


  /**
   * 특정 사용자의 메시지 조회
   */
  async getUserMessages(userId) {
    try {
      const messagesRef = collection(db, this.messagesCollection);
      const q = query(
        messagesRef,
        where('recipientId', '==', userId),
        orderBy('timestamp', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return messages;
    } catch (error) {
      console.error('사용자 메시지 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 관리자 메시지 전송
   */
  async sendAdminMessage(recipientId, recipientName, content) {
    try {
      console.log('🔍 [ADMIN] sendAdminMessage 호출됨:', {
        recipientId,
        recipientName,
        content: content.substring(0, 50) + '...'
      });
      
      const messageData = {
        senderId: 'admin@kairos.com',
        senderName: 'Kairos Admin',
        recipientId: recipientId?.toLowerCase(), // 이메일을 소문자로 저장 (대소문자 통일)
        recipientName: recipientName,
        content: content.trim(),
        timestamp: serverTimestamp(),
        read: false,
        type: 'admin_message',
        isAdminMessage: true
      };

      console.log('🔍 [ADMIN] 저장할 메시지 데이터:', messageData);
      const docRef = await addDoc(collection(db, this.messagesCollection), messageData);
      console.log('✅ [ADMIN] 메시지 저장 완료:', docRef.id);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('관리자 메시지 전송 실패:', error);
      throw error;
    }
  }

  /**
   * 다중 사용자에게 관리자 메시지 전송
   */
  async sendBulkAdminMessage(recipients, content) {
    try {
      console.log('📤 [ADMIN] 다중 사용자 메시지 전송 시작:', recipients.length);
      
      const sendPromises = recipients.map(recipient => 
        this.sendAdminMessage(recipient.email?.toLowerCase(), recipient.name, content)
      );
      
      const results = await Promise.all(sendPromises);
      console.log('✅ [ADMIN] 다중 사용자 메시지 전송 완료:', results.length);
      
      return { success: true, sentCount: results.length };
    } catch (error) {
      console.error('❌ [ADMIN] 다중 메시지 전송 실패:', error);
      throw error;
    }
  }

  /**
   * 전체 사용자에게 관리자 메시지 전송
   */
  async sendToAllUsers(content) {
    try {
      console.log('📢 전체 사용자 메시지 전송 시작...');
      
      // 모든 사용자 조회
      const users = await this.getUsers();
      console.log(`👥 총 ${users.length}명의 사용자에게 메시지 전송`);
      
      if (users.length === 0) {
        throw new Error('전송할 사용자가 없습니다.');
      }

      // 각 사용자에게 메시지 전송 (이메일을 recipientId로 사용, 소문자 변환)
      const sendPromises = users.map(user => 
        this.sendAdminMessage(user.email?.toLowerCase(), user.displayName || user.name, content)
      );
      
      const results = await Promise.all(sendPromises);
      console.log(`✅ 전체 사용자 메시지 전송 완료: ${results.length}명`);
      
      return { 
        success: true, 
        sentCount: results.length,
        totalUsers: users.length 
      };
    } catch (error) {
      console.error('❌ 전체 사용자 메시지 전송 실패:', error);
      throw error;
    }
  }

  /**
   * 메시지 삭제
   */
  async deleteMessage(messageId) {
    try {
      console.log('🗑️ [ADMIN] 메시지 삭제 시도:', {
        messageId: messageId,
        collection: this.messagesCollection,
        fullPath: `${this.messagesCollection}/${messageId}`
      });
      
      const docRef = doc(db, this.messagesCollection, messageId);
      console.log('📂 [ADMIN] 삭제할 문서 참조:', docRef.path);
      
      await deleteDoc(docRef);
      console.log('✅ [ADMIN] 메시지 삭제 성공:', messageId);
      
      return { success: true };
    } catch (error) {
      console.error('❌ [ADMIN] 메시지 삭제 실패:', {
        messageId: messageId,
        error: error,
        errorMessage: error.message,
        errorCode: error.code
      });
      throw error;
    }
  }

  /**
   * 사용자의 모든 메시지 삭제
   */
  async deleteUserMessages(userId) {
    try {
      const userMessages = await this.getUserMessages(userId);
      const deletePromises = userMessages.map(msg => 
        this.deleteMessage(msg.id)
      );
      
      await Promise.all(deletePromises);
      return { success: true, deletedCount: userMessages.length };
    } catch (error) {
      console.error('사용자 메시지 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 메시지 읽음 처리
   */
  async markAsRead(messageId) {
    try {
      await updateDoc(doc(db, this.messagesCollection, messageId), {
        read: true
      });
      return { success: true };
    } catch (error) {
      console.error('메시지 읽음 처리 실패:', error);
      throw error;
    }
  }

  /**
   * 메시지 통계 조회
   */
  async getMessageStats() {
    try {
      const messagesRef = collection(db, this.messagesCollection);
      const snapshot = await getDocs(messagesRef);
      const messages = snapshot.docs.map(doc => doc.data());
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const stats = {
        total: messages.length,
        unread: messages.filter(msg => !msg.read).length,
        read: messages.filter(msg => msg.read).length,
        today: messages.filter(msg => {
          const msgDate = msg.timestamp?.toDate ? msg.timestamp.toDate() : new Date(msg.timestamp);
          return msgDate >= today;
        }).length,
        adminMessages: messages.filter(msg => msg.isAdminMessage).length
      };
      
      return stats;
    } catch (error) {
      console.error('메시지 통계 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 메시지 검색
   */
  async searchMessages(searchQuery, filters = {}) {
    try {
      const messagesRef = collection(db, this.messagesCollection);
      let q = query(messagesRef, orderBy('timestamp', 'desc'), limit(100));
      
      const snapshot = await getDocs(q);
      let messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // 클라이언트 사이드 필터링
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        messages = messages.filter(msg => 
          msg.content.toLowerCase().includes(query) ||
          msg.senderName.toLowerCase().includes(query) ||
          msg.recipientName.toLowerCase().includes(query)
        );
      }
      
      if (filters.read !== undefined) {
        messages = messages.filter(msg => msg.read === filters.read);
      }
      
      if (filters.isAdminMessage !== undefined) {
        messages = messages.filter(msg => msg.isAdminMessage === filters.isAdminMessage);
      }
      
      return messages;
    } catch (error) {
      console.error('메시지 검색 실패:', error);
      throw error;
    }
  }

  /**
   * 사용자별 메시지 통계
   */
  async getUserMessageStats() {
    try {
      const users = await this.getUsers();
      const messagesRef = collection(db, this.messagesCollection);
      const snapshot = await getDocs(messagesRef);
      const messages = snapshot.docs.map(doc => doc.data());
      
      const userStats = users.map(user => {
        const userMessages = messages.filter(msg => 
          msg.senderId === (user.email || user.id) || 
          msg.recipientId === (user.email || user.id)
        );
        
        return {
          userId: user.id,
          userEmail: user.email,
          userName: user.displayName || user.name || user.email,
          totalMessages: userMessages.length,
          sentMessages: userMessages.filter(msg => msg.senderId === (user.email || user.id)).length,
          receivedMessages: userMessages.filter(msg => msg.recipientId === (user.email || user.id)).length,
          unreadMessages: userMessages.filter(msg => 
            msg.recipientId === (user.email || user.id) && !msg.read
          ).length
        };
      });
      
      return userStats;
    } catch (error) {
      console.error('사용자 메시지 통계 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 공지사항을 모든 사용자에게 쪽지로 발송
   * @param {Object} noticeData - 공지사항 데이터
   * @returns {Object} 발송 결과
   */
  async sendNoticeToAllUsers(noticeData) {
    try {
      console.log('📝 공지사항 쪽지 발송 시작:', noticeData.title);
      console.log('📝 공지사항 데이터:', noticeData);

      // 모든 사용자 조회
      const allUsers = await this.getAllUsers();
      console.log('👥 조회된 사용자 수:', allUsers.length);
      console.log('👥 사용자 목록:', allUsers.map(u => ({ email: u.email, name: u.name })));
      
      if (allUsers.length === 0) {
        console.warn('⚠️ 발송할 사용자가 없습니다.');
        return { success: false, reason: 'no_users' };
      }

      // 발신자 정보 설정
      const senderInfo = this.getSenderInfo(noticeData.sender);
      
      let totalSuccess = 0;
      let totalFailure = 0;
      const results = [];

      // 각 사용자에게 쪽지 발송 (강화된 중복 체크 포함)
      for (const user of allUsers) {
        try {
          console.log(`🔍 사용자 ${user.email}에 대한 쪽지 발송 시작`);
          console.log(`👤 사용자 정보:`, { id: user.id, email: user.email, name: user.name });
          
          // 중복 체크 완전 제거 - 메시지 발송만 진행
          console.log(`🔍 사용자 ${user.email}에 대한 쪽지 발송 진행`);

          const messageContent = noticeData.title;
          
          const messageData = {
            senderId: 'admin@kairos.com',
            senderName: senderInfo.name,
            recipientId: user.email,
            recipientName: user.name || user.email,
            content: messageContent,
            timestamp: serverTimestamp(),
            read: false,
            type: 'notice_message',
            isAdminMessage: false, // 공지사항 쪽지는 관리자 쪽지가 아님
            noticeId: noticeData.id || `notice_${Date.now()}`, // noticeId 필수 설정
            noticeTitle: noticeData.title,
            noticeContent: noticeData.content,
            priority: noticeData.priority || 'normal',
            category: noticeData.category || 'general'
          };

          const docRef = await addDoc(collection(db, this.messagesCollection), messageData);
          totalSuccess++;
          
          console.log(`✅ 쪽지 발송 성공: ${user.email} (문서 ID: ${docRef.id})`);
          console.log(`📝 발송된 메시지 데이터:`, messageData);
        } catch (error) {
          totalFailure++;
          console.error(`❌ 쪽지 발송 실패: ${user.email}`, error);
        }
      }

      console.log(`✅ 공지사항 쪽지 발송 완료: 성공 ${totalSuccess}건, 실패 ${totalFailure}건`);
      
      return {
        success: true,
        sentCount: totalSuccess,
        failureCount: totalFailure,
        totalUsers: allUsers.length,
        message: 'Notice messages sent successfully'
      };

    } catch (error) {
      console.error('❌ 공지사항 쪽지 발송 실패:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 모든 사용자 조회
   * @returns {Array} 사용자 배열
   */
  async getAllUsers() {
    try {
      const hardcodedAppId = 'kairos-platform-1ffde';
      const allUsers = [];
      const processedUserIds = new Set();
      
      // 유효한 경로만 검색 - 실제 사용자 데이터가 저장된 경로들
      const userCollectionPaths = [
        `artifacts/${hardcodedAppId}/users`, // 사용자별 서브컬렉션들
        'users', // 루트 레벨 사용자 컬렉션
        `artifacts/${hardcodedAppId}/userProfiles`, // 사용자 프로필 컬렉션
        'userProfiles' // 루트 레벨 사용자 프로필
      ];
      
      console.log('🔍 사용자 조회 시작 - 경로들:', userCollectionPaths);
      
      for (const path of userCollectionPaths) {
        try {
          console.log(`🔍 경로 ${path} 조회 중...`);
          const usersRef = collection(db, path);
          const snapshot = await getDocs(usersRef);
          
          console.log(`📊 경로 ${path}에서 ${snapshot.docs.length}개 문서 발견`);
          
          snapshot.docs.forEach(doc => {
            const userData = doc.data();
            const userId = doc.id;
            
            console.log(`👤 사용자 ${userId}:`, {
              email: userData.email,
              name: userData.name || userData.displayName,
              hasEmail: !!userData.email
            });
            
            if (!processedUserIds.has(userId) && userData.email) {
              allUsers.push({
                id: userId,
                email: userData.email,
                name: userData.name || userData.displayName || userData.email,
                ...userData
              });
              processedUserIds.add(userId);
              console.log(`✅ 사용자 ${userId} 추가됨`);
            } else {
              console.log(`⚠️ 사용자 ${userId} 건너뛰기:`, {
                alreadyProcessed: processedUserIds.has(userId),
                hasEmail: !!userData.email
              });
            }
          });
        } catch (pathError) {
          console.warn(`사용자 경로 ${path} 조회 실패:`, pathError);
        }
      }
      
      console.log(`🎯 최종 사용자 수: ${allUsers.length}`);
      return allUsers;
    } catch (error) {
      console.error('사용자 조회 실패:', error);
      return [];
    }
  }

  /**
   * 발신자 정보 가져오기
   * @param {string} sender - 발신자 타입
   * @returns {Object} 발신자 정보
   */
  getSenderInfo(sender) {
    const senderMap = {
      'admin': {
        name: '👨‍💼 Administrator'
      },
      'myanmar_labor': {
        name: '🇲🇲 Myanmar Ministry of Labour'
      },
      'system': {
        name: '🤖 System'
      },
      'operator': {
        name: '🏛️ System Operator'
      },
      'support': {
        name: '🆘 Support Team'
      }
    };
    
    return senderMap[sender] || senderMap['admin'];
  }
}

export default new MessageService();